import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  User,
  FileText,
  Paperclip,
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  Shield,
  ExternalLink,
  CalendarRange,
  Banknote,
} from "lucide-react";
import {
  getPlanById,
  approvePlan,
  rejectPlan,
} from "../../../api/hrm/plans/info";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";

const STATUS_LABEL = {
  draft: "Nháp",
  pending: "Chờ xử lý",
  waiting_approval: "Chờ phê duyệt",
  approved: "Đã duyệt",
  rejected: "Từ chối",
};

const STATUS_BADGE = {
  draft: "bg-gray-50 border-gray-100 text-gray-400",
  pending: "bg-sky-50 border-sky-100 text-sky-600",
  waiting_approval: "bg-amber-50 border-amber-100 text-amber-700",
  approved: "bg-green-50 border-green-100 text-green-600",
  rejected: "bg-rose-50 border-rose-100 text-rose-600",
};

const CATEGORY_LABEL = {
  branch: "Chi nhánh",
  department: "Phòng ban",
  personal: "Cá nhân",
};

const TYPE_LABEL = {
  finance: "Tài chính",
  work: "Công việc",
};

const norm = (s) => (s || "").toLowerCase();

const PlanViewModal = ({ planId, onClose, onUpdated, onRequestDeleteDraft }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const load = useCallback(() => {
    if (!planId) return;
    setLoading(true);
    setPlan(null);
    getPlanById(planId)
      .then((res) => setPlan(res))
      .catch((err) => {
        console.error("Lỗi load chi tiết kế hoạch:", err);
        showErrorToast("Không tải được chi tiết kế hoạch");
      })
      .finally(() => setLoading(false));
  }, [planId]);

  useEffect(() => {
    setNote("");
    load();
  }, [planId, load]);

  useEffect(() => {
    if (!planId) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !acting) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [planId, onClose, acting]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatCurrency = (num) => {
    if (num == null || num === "") return "0 ₫";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(Number(num));
  };

  const statusKey = norm(plan?.status);
  const statusBadgeClass =
    STATUS_BADGE[statusKey] || "bg-gray-50 border-gray-100 text-gray-400";

  const canReview =
    hasPermission("hrm.plans", "A") &&
    plan &&
    ["pending", "waiting_approval"].includes(statusKey);

  const isOwnerDraft =
    plan &&
    statusKey === "draft" &&
    String(plan.creator_id || "") === String(currentUser?.id || "");

  const handleApprove = async () => {
    if (!planId) return;
    setActing(true);
    try {
      await approvePlan(planId, { note: note.trim() || undefined });
      showSuccessToast("Đã phê duyệt kế hoạch");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || "Không thể phê duyệt");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!planId) return;
    const ok = await showConfirmDialog({
      title: "Từ chối kế hoạch",
      text: "Xác nhận từ chối kế hoạch này?",
      confirmText: "Từ chối",
      cancelText: "Hủy",
    });
    if (!ok.isConfirmed) return;
    setActing(true);
    try {
      await rejectPlan(planId, { note: note.trim() || undefined });
      showSuccessToast("Đã từ chối kế hoạch");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || "Không thể từ chối");
    } finally {
      setActing(false);
    }
  };

  const infoItem = (Icon, label, value, iconColor = "text-gray-400") => (
    <div className="flex items-center gap-3 rounded-lg border border-transparent p-2 transition-colors hover:border-gray-100 hover:bg-gray-50">
      <div className={`rounded-lg bg-gray-50 p-2 ${iconColor}`}>
        {Icon && <Icon size={18} />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-tight text-gray-400">{label}</p>
        <p className="break-words text-[13px] font-semibold text-gray-700">{value ?? "—"}</p>
      </div>
    </div>
  );

  const sectionTitle = (text) => (
    <h3 className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-red-500">
      <div className="h-4 w-1 rounded-full bg-red-500" />
      {text}
    </h3>
  );

  if (!planId) return null;

  const titleInitial = loading
    ? null
    : (plan?.title || "?").trim().charAt(0).toUpperCase() || "—";

  return (
    <AnimatePresence>
      {planId && (
        <>
          <motion.div
            key="plan-view-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
            onClick={() => !acting && onClose?.()}
          >
            <motion.div
              key={planId}
              role="dialog"
              aria-modal="true"
              aria-labelledby="plan-view-modal-title"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              {/* Header — cùng phong cách StudentDetailModal */}
              <div className="relative flex items-center justify-between border-b border-gray-100 p-6">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-2xl font-black text-red-600 shadow-sm">
                    {loading ? (
                      <Loader2 className="h-7 w-7 animate-spin text-red-600" strokeWidth={2.25} />
                    ) : (
                      titleInitial
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2
                      id="plan-view-modal-title"
                      className="line-clamp-2 text-xl font-bold leading-tight text-gray-800"
                    >
                      {loading ? "Đang tải…" : plan?.title || "—"}
                    </h2>
                    {!loading && plan && (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="rounded px-2 py-0.5 text-[10px] font-bold uppercase text-gray-500 bg-gray-100">
                          ID: {String(plan.id || "").slice(0, 8)}
                        </span>
                        <span
                          className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${statusBadgeClass}`}
                        >
                          {STATUS_LABEL[statusKey] || plan.status}
                        </span>
                        {plan.plan_type && (
                          <span className="rounded border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-600">
                            {TYPE_LABEL[norm(plan.plan_type)] || plan.plan_type}
                          </span>
                        )}
                        {plan.plan_category && (
                          <span className="rounded border border-purple-100 bg-purple-50 px-2 py-0.5 text-[10px] font-bold uppercase text-purple-600">
                            {CATEGORY_LABEL[norm(plan.plan_category)] || plan.plan_category}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {isOwnerDraft && onRequestDeleteDraft && plan && !loading && (
                    <button
                      type="button"
                      onClick={() => onRequestDeleteDraft(plan)}
                      disabled={acting}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2 text-xs font-bold text-red-600 transition-all hover:bg-red-50 active:scale-[0.98] disabled:opacity-50"
                    >
                      <Trash2 size={16} />
                      Xóa nháp
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={acting}
                    className="shrink-0 rounded-full p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                    aria-label="Đóng"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Body — 2 cột có scroll như StudentDetailModal */}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
                {loading ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
                    <Loader2 className="h-10 w-10 animate-spin text-red-600" strokeWidth={2.25} />
                    <p className="text-sm font-semibold text-gray-500">Đang tải kế hoạch…</p>
                  </div>
                ) : !plan ? (
                  <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                    <p className="text-[13px] font-semibold text-red-700">Không tìm thấy dữ liệu</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 p-4">
                      <p className="text-[12px] font-semibold leading-relaxed text-red-800">
                        <span className="font-black">Gợi ý luồng:</span> kế hoạch{" "}
                        <strong className="text-green-700">đã duyệt</strong> có thể gắn với đề nghị{" "}
                        <strong>tạm ứng / thanh toán</strong> (module Finance — triển khai sau).
                      </p>
                    </div>

                    <div className="grid h-full min-h-0 flex-1 grid-cols-1 gap-6 md:grid-cols-2">
                      <div className="min-h-0 space-y-4 overflow-y-auto pr-0 md:pr-2">
                        {sectionTitle("Thông tin chung")}
                        <div className="space-y-1">
                          {infoItem(FileText, "Tiêu đề", plan.title, "text-blue-500")}
                          <div className="flex items-start gap-3 rounded-lg border border-transparent p-2 transition-colors hover:border-gray-100 hover:bg-gray-50">
                            <div className="rounded-lg bg-gray-50 p-2 text-purple-500">
                              <User size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-bold uppercase tracking-tight text-gray-400">
                                Người tạo
                              </p>
                              <p className="break-words text-[13px] font-semibold text-gray-800">
                                {plan.creator_name || "—"}
                              </p>
                              <p className="mt-1 text-[12px] leading-snug text-gray-600">
                                {plan.branch_name || "—"}
                                <span className="mx-1.5 text-gray-300">•</span>
                                {plan.department_name || "—"}
                              </p>
                            </div>
                          </div>
                          {infoItem(
                            CalendarRange,
                            "Thời gian",
                            `${formatDate(plan.start_date)} → ${formatDate(plan.end_date)}`,
                            "text-gray-500"
                          )}
                        </div>

                        <div>
                          {sectionTitle("Nội dung tổng quan")}
                          {plan.content ? (
                            <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 text-[13px] font-semibold leading-relaxed text-gray-700 whitespace-pre-wrap">
                              {plan.content}
                            </div>
                          ) : (
                            <p className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/40 p-4 text-[13px] font-medium italic text-gray-500">
                              Chưa có nội dung tổng quan.
                            </p>
                          )}
                        </div>

                        <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 via-white to-red-50/30 p-5 shadow-sm">
                          <div className="flex items-center gap-2 text-red-600">
                            <div className="rounded-lg bg-white/80 p-2 shadow-sm">
                              <Banknote size={22} strokeWidth={2.25} />
                            </div>
                            <p className="text-[11px] font-black uppercase tracking-widest text-red-600">
                              Ngân sách dự kiến
                            </p>
                          </div>
                          <p className="mt-3 break-words text-2xl font-black tracking-tight text-red-700 md:text-3xl">
                            {formatCurrency(plan.budget)}
                          </p>
                        </div>
                      </div>

                      <div className="min-h-0 space-y-4 overflow-y-auto pl-0 md:pl-2">
                        <div>
                          {sectionTitle("Tệp đính kèm")}
                          {plan.attachment ? (
                            <a
                              href={plan.attachment}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-red-100 transition-all hover:bg-red-700 active:scale-95"
                            >
                              <ExternalLink size={18} />
                              Mở tệp
                            </a>
                          ) : (
                            <p className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/40 p-4 text-[13px] font-medium italic text-gray-500">
                              Không có tệp đính kèm.
                            </p>
                          )}
                        </div>

                        <div>
                          {sectionTitle("Chi tiết mục tiêu")}
                          {plan.items && plan.items.length > 0 ? (
                            <div className="space-y-2">
                              {plan.items.map((item, idx) => (
                                <div
                                  key={item.id || idx}
                                  className="rounded-2xl border border-red-100 bg-red-50 p-3"
                                >
                                  <p className="text-[11px] font-black uppercase tracking-tight text-red-700">
                                    Mục tiêu {idx + 1}
                                  </p>
                                  <p className="mt-1 text-[13px] font-semibold text-gray-800">
                                    {item.target}
                                  </p>
                                  <p className="mt-1.5 text-[11px] text-gray-600">
                                    <span className="font-bold text-gray-500">Phụ trách:</span>{" "}
                                    {item.assignee || "—"}
                                  </p>
                                  {item.attachment && (
                                    <a
                                      href={item.attachment}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-red-700 underline"
                                    >
                                      <Paperclip size={14} />
                                      Xem tệp
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
                              <p className="text-[12px] font-semibold text-red-700">
                                Chưa có mục tiêu chi tiết.
                              </p>
                            </div>
                          )}
                        </div>

                        {plan.approvals?.length > 0 && (
                          <div>
                            {sectionTitle("Lịch sử phê duyệt")}
                            <div className="space-y-2">
                              {plan.approvals.map((a, idx) => {
                                const ok = norm(a.status) === "approved";
                                return (
                                  <div
                                    key={idx}
                                    className="rounded-2xl border border-red-100 bg-red-50 p-3"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        {ok ? (
                                          <CheckCircle2 className="text-emerald-600" size={16} />
                                        ) : (
                                          <XCircle className="text-red-600" size={16} />
                                        )}
                                        <p className="text-[11px] font-black uppercase tracking-tight text-red-700">
                                          {a.approver_name}
                                        </p>
                                      </div>
                                      <p className="text-[11px] font-bold text-gray-500">
                                        {formatDate(a.timestamp)}
                                      </p>
                                    </div>
                                    <p className="mt-1 text-[10px] font-semibold uppercase text-gray-500">
                                      {a.status}
                                    </p>
                                    {a.note ? (
                                      <p className="mt-1.5 text-[11px] font-semibold text-gray-800">
                                        {a.note}
                                      </p>
                                    ) : (
                                      <p className="mt-1.5 text-[11px] italic text-gray-500">
                                        Không có ghi chú
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {canReview && plan && !loading && (
                <div className="shrink-0 border-t border-amber-100 bg-amber-50/80 px-6 py-4">
                  <h3 className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-amber-800">
                    <Shield className="h-4 w-4" strokeWidth={2.5} />
                    Phê duyệt kế hoạch
                  </h3>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-tight text-gray-500">
                    Ghi chú (không bắt buộc)
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    disabled={acting}
                    className="mb-4 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-800 outline-none transition focus:border-red-300 disabled:opacity-60"
                    placeholder="Nhận xét cho nhân viên…"
                  />
                  <div className="flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      disabled={acting}
                      onClick={handleReject}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-red-200 bg-white px-6 py-3 text-sm font-bold text-red-600 transition-all hover:bg-red-50 active:scale-[0.98] disabled:opacity-50"
                    >
                      <XCircle size={18} />
                      Từ chối
                    </button>
                    <button
                      type="button"
                      disabled={acting}
                      onClick={handleApprove}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-red-100 transition-all hover:bg-red-700 active:scale-95 disabled:opacity-50"
                    >
                      <CheckCircle2 size={18} />
                      Duyệt
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PlanViewModal;
